import { useEffect, useState } from "react";
import { BACKEND_URL, formatDate } from "@/lib/api";
import { Link } from "react-router-dom";
import { Calendar, MapPin, HandHeart, ImageOff, ArrowRight } from "lucide-react";
import { HarborWellFormHeader } from "@/components/HarborWellFormHeader";

/**
 * Public, no-auth impact gallery. Only outreach photos explicitly marked
 * `is_public` by an admin/ED appear here. Never shows Care Seeker data.
 */
export default function Impact() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/public/gallery`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setPhotos)
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="impact-page">
      {/* Header */}
      <header className="border-b border-[#E5E5E0] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-5">
          <HarborWellFormHeader
            eyebrow="Connect"
            size="md"
            testId="impact-brand-header"
            rightSlot={
              <Link
                to="/login"
                data-testid="impact-staff-login"
                className="text-xs font-medium text-[#1E3A8A] inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] hover:bg-white transition-colors"
              >
                Staff login
                <ArrowRight className="w-3 h-3" strokeWidth={2} />
              </Link>
            }
          />
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-8">
        <div className="hw-eyebrow mb-3">Impact in eastern North Carolina</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#0F172A] leading-tight max-w-3xl">
          The community we&apos;re caring for, in pictures.
        </h1>
        <p className="text-[#334155] mt-4 max-w-2xl">
          Moments from HarborWell&apos;s outreach events, wellness check-ins, and community gatherings
          — shared by our team to celebrate the people we serve and the partners who make it possible.
        </p>
      </section>

      {/* Gallery */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        {loading ? (
          <div className="text-center py-16 text-sm text-[#1E3A8A]">Loading gallery…</div>
        ) : photos.length === 0 ? (
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center" data-testid="impact-empty">
            <ImageOff className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-1">More moments coming soon</h3>
            <p className="text-sm text-[#334155] max-w-md mx-auto">
              We&apos;re constantly out in the community. Check back soon to see how HarborWell is showing up
              for eastern NC families.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="impact-gallery">
            {photos.map((p) => (
              <article
                key={p.id}
                data-testid={`impact-photo-${p.id}`}
                className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden hw-card-hover"
              >
                <div className="aspect-[4/3] bg-[#F9F8F6] relative">
                  <img
                    src={`${BACKEND_URL}/api/public/gallery/${p.id}/image`}
                    alt={p.event?.title || "Outreach event"}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="hw-eyebrow text-[10px] mb-1.5">
                    {(p.event?.activity_type || "Outreach").replace(/_/g, " ")}
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[#0F172A] leading-snug">
                    {p.event?.title || "HarborWell outreach"}
                  </h3>
                  <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#1E3A8A]">
                    {p.event?.activity_date && (
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" strokeWidth={1.75} />
                        {formatDate(p.event.activity_date)}
                      </span>
                    )}
                    {p.event?.location && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" strokeWidth={1.75} />
                        {p.event.location}
                        {p.event.county && `, ${p.event.county} County`}
                      </span>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5E5E0] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
          <div className="text-xs text-[#1E3A8A]">
            © {new Date().getFullYear()} HarborWell. A 501(c)(3) nonprofit serving eastern North Carolina.
          </div>
          <div className="text-xs text-[#1E3A8A]">
            Photos shared with consent from our community partners.
          </div>
        </div>
      </footer>
    </div>
  );
}
